import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAsyncFn } from 'react-use';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import Donut from 'components/Donut';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import StatisticsInfo from 'components/StatisticsInfo';
import withTable from 'HOC/withTable';
import withTableProps from 'HOC/types/withTable';
import { makeStyles } from '@material-ui/core/styles';
import {
  toggleFilterArrayValue,
  transformTableData,
  transformToPastByTrainingModules,
  transformToComplianceStatusBuckets,
} from 'utils';
import { exportTrainingSessionStatistics, getTrainingSessionStatistics } from 'http/trainingSessionStatistics';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import getTableConfig from './helpers/getTableConfig';

const pageAlias = 'TrainingSessionStatistics';

const useStyles = makeStyles(theme => ({
  statisticsSection: {
    display: 'flex',
    margin: '70px 0 30px 0',
    alignItems: 'center',
  },
  statisticsLeft: {
    flexBasis: '50%',
  },
  statisticsRight: {
    flexBasis: '50%',
  },
  pageWrapper: {
    padding: theme.spacing(1, 4),
  },
  donutRoot: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  donutLegendWrapper: {},
  donutWrapper: {},
  tableRoot: {
    padding: '10px 0',
  },
  helperText: {
    margin: '8px 0 0',
    maxWidth: 420,
    color: theme.palette.text.grey,
    lineHeight: 1.5,
    fontSize: '0.9rem',
  },
}));

const TrainingSessionStatistics = ({ tableData, total, diagrams }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation('common');
  const tableConfig = getTableConfig();
  const {
    lang,
    profile: { role },
  } = useSelector(state => state.auth);
  const [donutDataByCompliance, setDonutDataByCompliance] = useState([]);
  const [donutDataByModules, setDonutDataByModules] = useState([]);
  const [statisticsInfo, setStatisticsInfo] = useState([]);
  const [columns, statistics] = transformTableData(tableConfig, tableData.data.items);
  const [exportState, exportStatisticsFile] = useAsyncFn(async () => {
    await exportTrainingSessionStatistics(tableData.getRequestParams({ includePagination: false }));
  }, [tableData]);

  const handleComplianceFilter = complianceStatus => {
    if (!complianceStatus) {
      return;
    }

    const nextValue = toggleFilterArrayValue(tableData.filtering.complianceStatuses, complianceStatus);
    if (!nextValue.length) {
      tableData.handleRemoveFilter('complianceStatuses');
      return;
    }

    tableData.handleFiltering('complianceStatuses', nextValue);
  };

  const handleTrainingModuleFilter = trainingModuleId => {
    if (!trainingModuleId) {
      return;
    }

    const nextValue = toggleFilterArrayValue(tableData.filtering.trainingModuleIds, trainingModuleId);
    if (!nextValue.length) {
      tableData.handleRemoveFilter('trainingModuleIds');
      return;
    }

    tableData.handleFiltering('trainingModuleIds', nextValue);
  };

  const makeActions =
    role !== ROLES.TRAINER
      ? [
          {
            title: t('buttons.view'),
            onClick: event =>
              history.push({
                pathname: `${routesAliases.editStudent}/${event.currentTarget.dataset.userid}`,
              }),
          },
        ]
      : undefined;

  useEffect(() => {
    tableData.handleChangeFetchMethod(pageAlias, getTrainingSessionStatistics);
  }, [lang]);

  useEffect(() => {
    setStatisticsInfo([
      {
        title: t('trainingSessionStatistics.totalModules'),
        value: total.modules,
        subtitle: t('trainingSessionStatistics.subtitleModules'),
      },
      {
        title: t('statistics.totalStudents'),
        value: total.students,
        subtitle: t('statistics.subtitlePeople'),
      },
    ]);
  }, [total, t]);

  useEffect(() => {
    setDonutDataByCompliance(transformToComplianceStatusBuckets(diagrams.passed));
    setDonutDataByModules(transformToPastByTrainingModules(diagrams.students, 4));
  }, [diagrams]);

  const complianceStatusOptions = useMemo(() => {
    const backendOptions = tableData.filterOptions.complianceStatuses || [];
    return backendOptions.map(option => ({
      ...option,
      label: t(`compliance.statuses.${option.value}`, { defaultValue: option.label }),
    }));
  }, [tableData.filterOptions.complianceStatuses, t]);

  return (
    <PageLayout
      pageTitle={t('trainingSessionStatistics.pageTitle')}
      contentHeader={t('statistics.trainingRequirementsOverview')}
      actionSectionLeft={[
        {
          type: 'component',
          Component: <StatisticsInfo items={statisticsInfo} />,
        },
      ]}
      actionSectionRight={[
        {
          type: 'button',
          icon: 'import_export',
          title: t('commonActions.actionExport'),
          onClick: exportStatisticsFile,
          disabled: exportState.loading,
        },
      ]}
      pageContentActions={[
        {
          type: 'component',
          Component: (
            <TableFilterToolbar
              filters={[
                {
                  type: 'search',
                  name: 'search',
                  label: t('tableFilters.search'),
                  placeholder: t('placeholders.searchStudent'),
                },
                {
                  type: 'multiSelect',
                  name: 'complianceStatuses',
                  label: t('complianceStatus'),
                  options: complianceStatusOptions,
                },
                {
                  type: 'multiSelect',
                  name: 'trainingModuleIds',
                  label: t('tablesLabels.trainingModule'),
                  options: tableData.filterOptions.trainingModules || [],
                },
                {
                  type: 'multiSelect',
                  name: 'trainingMethods',
                  label: t('tablesLabels.trainingPart'),
                  options: (tableData.filterOptions.trainingMethods || []).map(option => ({
                    ...option,
                    label: t(`trainingModules.tabs.${option.value}`, { defaultValue: option.label }),
                  })),
                },
              ]}
              filtering={tableData.filtering}
              onFilterChange={tableData.handleFiltering}
              onRemoveFilter={tableData.handleRemoveFilter}
              onClearFilters={tableData.handleClearFilters}
            />
          ),
        },
      ]}
      classes={{
        content: classes.pageWrapper,
      }}
    >
      <div className={classes.mainContent}>
        <div className={classes.statisticsSection}>
          {donutDataByCompliance && !!donutDataByCompliance.length && (
            <div className={classes.statisticsLeft}>
              <Donut
                donutTitle={t('legend.requiredMethodCompletion')}
                donutData={donutDataByCompliance}
                legendWidth={200}
                legendHeight={150}
                legendOrientation="vertical"
                legendItemsPerRow={4}
                classes={{
                  root: classes.donutRoot,
                  legendWrapper: classes.donutLegendWrapper,
                  donutWrapper: classes.donutWrapper,
                }}
                onDatumClick={handleComplianceFilter}
              />
              <p className={classes.helperText}>{t('trainingSessionStatistics.satisfactionWidgetHelp')}</p>
            </div>
          )}
          {donutDataByModules && !!donutDataByModules.length && (
            <div className={classes.statisticsRight}>
              <Donut
                donutTitle={t('legend.studentsTrainedByTrainingModule')}
                donutData={donutDataByModules}
                legendWidth={320}
                legendHeight={220}
                legendOrientation="vertical"
                legendItemsPerRow={4}
                shouldPercent={false}
                classes={{
                  root: classes.donutRoot,
                  legendWrapper: classes.donutLegendWrapper,
                  donutWrapper: classes.donutWrapper,
                }}
                onDatumClick={handleTrainingModuleFilter}
              />
              <p className={classes.helperText}>{t('trainingSessionStatistics.moduleVolumeWidgetHelp')}</p>
            </div>
          )}
        </div>

        <ModalBusyContainer busy={tableData.isFetchingTableData || exportState.loading}>
          <AppTable
            columns={columns}
            data={statistics}
            isLazyLoading
            totalCount={tableData.data.totalCount || 0}
            rowsPerPage={tableData.pageSize || 10}
            onChangePage={tableData.handleChangePage}
            currentPage={tableData.page}
            onColumnSorting={tableData.handleSorting}
            extraActionsKeys={['userId']}
            classes={{
              root: classes.tableRoot,
            }}
            actions={makeActions}
          />
        </ModalBusyContainer>
      </div>
    </PageLayout>
  );
};

TrainingSessionStatistics.defaultProps = {
  total: {},
  diagrams: {},
};

TrainingSessionStatistics.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  total: PropTypes.shape(),
  diagrams: PropTypes.shape(),
};

export default withTable({
  defaultSorting: {},
  sortingFieldsMapping: {
    ids: 'ids',
    fullName: 'fullName',
    trainingModuleName: 'trainingModuleName',
    trainingMethod: 'trainingMethod',
    complianceStatus: 'complianceStatus',
    performationDate: 'performedDate',
    expirationDate: 'expirationDate',
  },
  allowedFilters: ['search', 'trainingModuleIds', 'trainingMethods', 'complianceStatuses'],
  pageAlias,
  fetchingData: 'statData',
})(TrainingSessionStatistics);
